/**
 * Login page — JumpCloud OIDC, plus a preview-only bypass.
 *
 * Primary action: "Continue with JumpCloud". The local email/password
 * form was removed in #iter199.
 *
 * iter214: a "Skip login (preview only)" button renders ONLY when the
 * backend's /api/auth/bypass-status probe says the bypass is active
 * (requires AUTH_BYPASS_ENABLED=true AND a preview hostname — it can
 * never activate in production).
 *
 * `?next=/some/path` survives the OIDC round-trip via sessionStorage so
 * the user lands where they originally tried to go.
 *
 * `?error=<reason>` is added by the backend when the OIDC flow fails —
 * we surface a friendly message based on the reason code.
 */
import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Lock, ArrowRight, AlertTriangle, FlaskConical } from "lucide-react";
import { useAuth } from "@/auth/AuthContext";
import { authBypassStatus, authBypass, magicStatus } from "@/lib/api";
import FieldLoginOption from "@/auth/FieldLoginOption";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

// Friendly copy for the error codes the backend appends to /login?error=
const ERROR_COPY = {
  sso_denied:           "Sign-in was cancelled or denied in JumpCloud.",
  missing_code:         "JumpCloud didn't return a sign-in code. Try again.",
  state_missing:        "Your sign-in session expired. Try again.",
  state_invalid:        "Your sign-in session was invalid. Try again.",
  token_network:        "Couldn't reach JumpCloud. Check your connection and retry.",
  token_exchange_failed:"JumpCloud refused the sign-in. Contact your administrator.",
  no_id_token:          "JumpCloud didn't return an identity. Contact your administrator.",
  id_token_invalid:     "Sign-in token failed verification. Try again.",
  no_role:              "Your JumpCloud account isn't assigned to any ServiceOps group yet — contact your administrator.",
  user_disabled:        "Your ServiceOps account is disabled. Contact your administrator.",
};

const safeNext = (raw) => {
  if (!raw || typeof raw !== "string") return "/";
  if (!raw.startsWith("/")) return "/";
  if (raw.startsWith("//")) return "/";
  if (raw.includes(":")) return "/";
  return raw;
};

export default function Login() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [redirecting, setRedirecting] = useState(false);
  const [error, setError] = useState("");
  // iter214 — preview-only bypass availability (backend-decided).
  const [bypassAvailable, setBypassAvailable] = useState(false);
  const [bypassBusy, setBypassBusy] = useState(false);
  const [magic, setMagic] = useState({ magic_link_enabled: false, request_to_join_enabled: false });

  useEffect(() => {
    magicStatus().then(({ data }) => setMagic(data || {})).catch(() => { /* keep hidden */ });
  }, []);

  // Probe the backend once — in production this always returns
  // {enabled:false} so the button never renders there.
  useEffect(() => {
    let alive = true;
    authBypassStatus()
      .then(({ data }) => { if (alive) setBypassAvailable(!!data?.enabled); })
      .catch(() => { /* endpoint unreachable → keep hidden */ });
    return () => { alive = false; };
  }, []);

  // If we're already logged in, bounce.
  useEffect(() => {
    if (user) {
      const params = new URLSearchParams(location.search);
      navigate(safeNext(params.get("next")), { replace: true });
    }
  }, [user, navigate, location.search]);

  // Read ?error=... once on mount and convert to friendly copy.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const code = params.get("error");
    if (code) {
      setError(ERROR_COPY[code] || "Sign-in failed. Try again.");
    }
  }, [location.search]);

  const startSso = () => {
    setError("");
    setRedirecting(true);
    // Preserve `?next` across the full-page redirect via sessionStorage.
    const params = new URLSearchParams(location.search);
    const next = safeNext(params.get("next"));
    if (next && next !== "/") {
      try { sessionStorage.setItem("oidc_post_login_next", next); } catch (_) { /* private mode */ }
    }
    // Full browser navigation — XHR would be blocked by CORS on the
    // 302 to JumpCloud (per the OIDC playbook).
    window.location.href = `${BACKEND}/api/auth/jumpcloud/start`;
  };

  // iter214 — mint the preview session, then refresh the auth context;
  // the `user` effect above handles the ?next redirect.
  const doBypass = async () => {
    setError("");
    setBypassBusy(true);
    try {
      await authBypass();
      await refresh();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Preview bypass failed.");
      setBypassBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center p-6">
      <div
        className="w-full max-w-sm bg-white rounded-xl shadow-lg border border-slate-200 p-8 space-y-6"
        data-testid="login-card"
      >
        <div className="flex flex-col items-center text-center">
          <div className="w-12 h-12 bg-slate-900 text-white rounded-lg flex items-center justify-center mb-3">
            <Lock className="w-5 h-5" />
          </div>
          <h1 className="text-xl font-semibold text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>
            ServiceOps
          </h1>
          <p className="text-xs text-slate-500 mt-0.5">Sign in with your work account</p>
        </div>

        {error && (
          <div
            className="text-[12px] text-rose-800 bg-rose-50 border border-rose-200 rounded-md px-3 py-2 flex items-start gap-2"
            data-testid="login-error"
          >
            <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <Button
          type="button"
          onClick={startSso}
          disabled={redirecting}
          className="w-full h-11 bg-slate-900 hover:bg-slate-800 text-white text-[13.5px] font-semibold rounded-lg"
          data-testid="btn-login-jumpcloud"
        >
          {redirecting ? "Redirecting to JumpCloud…" : (
            <span className="flex items-center justify-center gap-2">
              Continue with JumpCloud
              <ArrowRight className="w-4 h-4" />
            </span>
          )}
        </Button>

        <p className="text-[11px] text-slate-500 text-center leading-relaxed">
          Access is granted via your JumpCloud group membership.<br />
          New here?{" "}
          <span className="text-slate-700 font-medium">
            Ask your administrator to add you to a ServiceOps group.
          </span>
        </p>

        {magic.magic_link_enabled && (
          <div className="pt-3 border-t border-slate-200">
            <FieldLoginOption requestToJoin={magic.request_to_join_enabled} />
          </div>
        )}

        {bypassAvailable && (
          <div className="pt-1 border-t border-dashed border-slate-200">
            <Button
              type="button"
              variant="outline"
              onClick={doBypass}
              disabled={bypassBusy || redirecting}
              className="w-full h-9 mt-3 text-[12px] font-medium text-amber-800 border-amber-300 bg-amber-50 hover:bg-amber-100 rounded-lg"
              data-testid="btn-login-bypass"
            >
              <span className="flex items-center justify-center gap-1.5">
                <FlaskConical className="w-3.5 h-3.5" />
                {bypassBusy ? "Signing in…" : "Skip login (preview only)"}
              </span>
            </Button>
            <p className="text-[10px] text-amber-700/70 text-center mt-1.5" data-testid="bypass-preview-note">
              Development shortcut — only active on preview hosts.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
